// Phase 5Z.10 — Driver vehicle handover (Übernahme) + DVIC walkaround
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Camera, Check, ChevronRight, ArrowLeft, Truck, Trash2, Eraser, AlertTriangle, Fingerprint, FileDown, Search } from "lucide-react";
import { toast } from "sonner";
import { fahrerApi, FAHRER_API_BASE } from "./lib/api";
import { compressImage } from "@/lib/imageCompressor";
import { fmtDateTime } from "@/lib/format";

const DVIC_POSITIONS = [
  "front", "front_right", "right", "rear_right",
  "rear", "rear_left", "left", "front_left",
];

export default function FahrerHandover() {
  const { t } = useTranslation();
  const [active, setActive] = useState(undefined);  // undefined = loading; null = none; object = active
  const nav = useNavigate();

  async function reload() {
    try {
      const { data } = await fahrerApi.get("/fahrer/active-handover");
      setActive(data);
    } catch {
      setActive(null);
    }
  }
  useEffect(() => { reload(); }, []);

  if (active === undefined) return <div className="text-zinc-500 text-sm py-8 text-center">{t("app.loading")}</div>;

  if (active === null) {
    return <PickVehicle onStarted={reload} />;
  }
  if (active.status !== "finalized") {
    // Still in DVIC/signature flow
    return <UebernahmeFlow handover={active} onDone={reload} />;
  }
  // Active finalized handover — show summary + return option
  return <ActiveHandoverSummary handover={active} onReload={reload} />;
}

// ─── 1. Pick a vehicle ─────────────────────────────────────────
function PickVehicle({ onStarted }) {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState(null);
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fahrerApi.get("/fahrer/available-vehicles").then(({ data }) => setVehicles(data));
  }, []);

  async function pick(v) {
    if (v.held_by_other && !window.confirm(t("handover.warning_other_driver", { name: v.held_by_other }))) return;
    setBusy(v.id);
    try {
      await fahrerApi.post("/fahrer/handover/start", { vehicle_id: v.id });
      onStarted();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally { setBusy(null); }
  }

  // Phase 5Z.24 — client-side search across plate/brand/model
  const filtered = vehicles?.filter((v) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase().trim();
    return (
      (v.license_plate || "").toLowerCase().includes(q) ||
      (v.brand || "").toLowerCase().includes(q) ||
      (v.model || "").toLowerCase().includes(q)
    );
  }) || null;

  return (
    <div className="space-y-4" data-testid="fahrer-pick-vehicle">
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex gap-3" data-testid="fahrer-no-active-card">
        <Truck className="w-5 h-5 text-amber-400 shrink-0" />
        <div>
          <h3 className="text-sm font-semibold text-zinc-100">{t("handover.no_active")}</h3>
          <p className="text-xs text-zinc-400 mt-0.5">{t("handover.no_active_hint")}</p>
        </div>
      </div>

      <div>
        <h2 className="text-base font-semibold text-zinc-100 mb-1">{t("handover.pick_vehicle")}</h2>
        <p className="text-xs text-zinc-500 mb-3">{t("handover.pick_vehicle_hint")}</p>

        {/* Phase 5Z.24 — Search field */}
        {vehicles && vehicles.length > 0 && (
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              data-testid="fahrer-veh-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("handover.search_placeholder", "Suche nach Kennzeichen, Marke, Modell...")}
              className="w-full pl-9 pr-3 py-2.5 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-amber-600 transition-colors"
            />
          </div>
        )}

        {vehicles === null && <div className="text-sm text-zinc-500 text-center py-6">{t("app.loading")}</div>}
        {vehicles && vehicles.length === 0 && <div className="text-sm text-zinc-500 text-center py-6 italic">—</div>}
        {filtered && filtered.length === 0 && vehicles?.length > 0 && (
          <div className="text-sm text-zinc-500 text-center py-6 italic" data-testid="fahrer-veh-no-results">
            {t("handover.no_search_results", "Keine Treffer")} — „{search}"
          </div>
        )}
        <div className="space-y-2">
          {filtered && filtered.map((v) => (
            <button
              key={v.id}
              onClick={() => pick(v)}
              disabled={busy === v.id}
              data-testid={`fahrer-veh-${v.license_plate?.replace(/\s/g,'')}`}
              className="w-full flex items-center gap-3 p-3.5 bg-zinc-900 border border-zinc-800 rounded-lg hover:border-amber-600 disabled:opacity-50 text-left transition-colors"
            >
              <div className="w-10 h-10 bg-zinc-800 rounded flex items-center justify-center">
                <Truck className="w-5 h-5 text-zinc-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm font-bold text-zinc-50">{v.license_plate}</div>
                <div className="text-xs text-zinc-400 truncate">{v.brand} {v.model}</div>
                {v.held_by_other && (
                  <div className="text-[10px] text-amber-400 mt-0.5 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {t("handover.held_by")}: {v.held_by_other}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] text-zinc-500">{t("handover.current_km")}</div>
                <div className="text-xs font-mono text-zinc-300">{(v.current_km || 0).toLocaleString()}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-500" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── 2. DVIC + details + signature flow ───────────────────────
function UebernahmeFlow({ handover, onDone }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);  // 1=DVIC, 2=details, 3=signature
  const [photos, setPhotos] = useState(handover.dvic_photos || []);
  const [km, setKm] = useState("");
  const [fuel, setFuel] = useState("");
  const [damages, setDamages] = useState("");
  const [notes, setNotes] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const photosCount = photos.length;
  const allPhotos = photosCount === DVIC_POSITIONS.length;

  function refreshPhotos() {
    fahrerApi.get(`/fahrer/handover/${handover.id}`).then(({ data }) => setPhotos(data.dvic_photos || []));
  }

  async function uploadPhoto(position, file) {
    // Phase 5Z.42 — phone cameras produce huge files; compress aggressively
    const compressed = await compressImage(file, { maxDimension: 1920, jpegQuality: 0.82 });
    const fd = new FormData();
    fd.append("position", position);
    fd.append("file", compressed);
    try {
      await fahrerApi.post(`/fahrer/handover/${handover.id}/dvic-photo`, fd);
      refreshPhotos();
      toast.success("OK");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    }
  }
  async function deletePhoto(position) {
    try {
      await fahrerApi.delete(`/fahrer/handover/${handover.id}/dvic-photo/${position}`);
      refreshPhotos();
    } catch { /* ignore */ }
  }

  async function finalize() {
    if (!allPhotos) { toast.error(t("handover.missing_photos")); setStep(1); return; }
    if (!km || Number(km) < 0) { toast.error(t("handover.missing_km")); setStep(2); return; }
    if (!signatureDataUrl) { toast.error(t("handover.missing_signature")); setStep(3); return; }
    setSubmitting(true);
    try {
      await fahrerApi.post(`/fahrer/handover/${handover.id}/finalize`, {
        km: Number(km),
        fuel_pct: fuel === "" ? null : Number(fuel),
        notes,
        damages,
        signature_dataurl: signatureDataUrl,
      });
      toast.success(t("handover.finalized"));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="fahrer-uebernahme-flow">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3.5">
        <div className="flex items-center gap-3">
          <Truck className="w-5 h-5 text-amber-400" />
          <div className="flex-1">
            <div className="text-xs text-zinc-500">{t("handover.title")}</div>
            <div className="text-sm font-mono font-bold text-zinc-100">{handover.vehicle_label}</div>
          </div>
        </div>
        <div className="mt-3 flex gap-1.5">
          {[1, 2, 3].map(n => (
            <div key={n} className={`flex-1 h-1 rounded ${n <= step ? "bg-amber-500" : "bg-zinc-800"}`} />
          ))}
        </div>
        <div className="mt-1 text-[10px] text-zinc-500 text-center">{t("handover.step", { n: step, total: 3 })}</div>
      </div>

      {step === 1 && (
        <DvicStep photos={photos} onUpload={uploadPhoto} onDelete={deletePhoto} t={t} handoverId={handover.id} />
      )}
      {step === 2 && (
        <DetailsStep
          km={km} setKm={setKm} fuel={fuel} setFuel={setFuel}
          damages={damages} setDamages={setDamages} notes={notes} setNotes={setNotes} t={t}
        />
      )}
      {step === 3 && (
        <SignatureStep value={signatureDataUrl} onChange={setSignatureDataUrl} t={t} />
      )}

      <div className="flex gap-2 pt-2">
        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            data-testid="fahrer-uebernahme-back"
            className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> {t("common.back")}
          </button>
        )}
        {step < 3 && (
          <button
            onClick={() => {
              if (step === 1 && !allPhotos) { toast.error(t("handover.missing_photos")); return; }
              if (step === 2 && (!km || Number(km) < 0)) { toast.error(t("handover.missing_km")); return; }
              setStep(step + 1);
            }}
            data-testid="fahrer-uebernahme-next"
            className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold"
          >
            Weiter →
          </button>
        )}
        {step === 3 && (
          <button
            onClick={finalize}
            disabled={submitting}
            data-testid="fahrer-uebernahme-finalize"
            className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-800 text-white rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5"
          >
            <Check className="w-4 h-4" />
            {submitting ? "…" : t("handover.finalize")}
          </button>
        )}
      </div>
    </div>
  );
}

function DvicStep({ photos, onUpload, onDelete, t, handoverId, phase = "start" }) {
  const captured = new Set(photos.map(p => p.position));
  const titleKey = phase === "return" ? "handover.return_dvic_title" : "handover.dvic_title";
  const subtitleKey = phase === "return" ? "handover.return_dvic_subtitle" : "handover.dvic_subtitle";
  return (
    <div className="space-y-3" data-testid={`fahrer-dvic-step-${phase}`}>
      <div>
        <h3 className="text-base font-semibold text-zinc-100">{t(titleKey, t("handover.dvic_title"))}</h3>
        <p className="text-xs text-zinc-500 mt-0.5">{t(subtitleKey, t("handover.dvic_subtitle"))}</p>
        <p className="text-[11px] text-amber-400 mt-1">{t("handover.dvic_done", { done: captured.size })}</p>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {DVIC_POSITIONS.map((pos, idx) => (
          <DvicSlot
            key={pos}
            idx={idx + 1}
            position={pos}
            captured={captured.has(pos)}
            handoverId={handoverId}
            onUpload={onUpload}
            onDelete={onDelete}
            t={t}
            phase={phase}
          />
        ))}
      </div>
    </div>
  );
}

function DvicSlot({ idx, position, captured, handoverId, onUpload, onDelete, t, phase = "start" }) {
  const inputRef = useRef(null);
  const label = t(`handover.positions.${position}`);
  const previewUrl = captured ? `${process.env.REACT_APP_BACKEND_URL}/api/fahrer/handover/${handoverId}/dvic-photo/${position}?phase=${phase}&_=${Date.now()}` : null;
  return (
    <div className={`relative aspect-[3/4] rounded-lg overflow-hidden border-2 ${captured ? "border-emerald-500/60" : "border-zinc-800 border-dashed"} bg-zinc-900`}>
      {captured && previewUrl && (
        <img src={previewUrl} alt={label} className="w-full h-full object-cover" />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid={`fahrer-dvic-input-${position}`}
        onChange={(e) => {
          if (e.target.files?.[0]) {
            onUpload(position, e.target.files[0]);
          }
        }}
      />
      <button
        onClick={() => inputRef.current?.click()}
        data-testid={`fahrer-dvic-slot-${position}`}
        className={`absolute inset-0 flex flex-col items-center justify-center gap-1 ${captured ? "bg-black/30 opacity-0 hover:opacity-100" : ""} transition-opacity`}
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${captured ? "bg-amber-600" : "bg-zinc-800"}`}>
          {captured ? <Camera className="w-4 h-4 text-white" /> : <span className="text-xs font-bold text-amber-400">{idx}</span>}
        </div>
        <span className="text-[10px] text-zinc-200 font-medium px-1 text-center leading-tight">{label}</span>
        {captured && <span className="text-[9px] text-amber-200 italic">{t("handover.dvic_retake")}</span>}
      </button>
      {captured && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(position); }}
          data-testid={`fahrer-dvic-del-${position}`}
          className="absolute top-1 right-1 w-6 h-6 bg-red-600/90 rounded-full flex items-center justify-center shadow"
        >
          <Trash2 className="w-3 h-3 text-white" />
        </button>
      )}
    </div>
  );
}

function DetailsStep({ km, setKm, fuel, setFuel, damages, setDamages, notes, setNotes, t }) {
  return (
    <div className="space-y-3" data-testid="fahrer-details-step">
      <h3 className="text-base font-semibold text-zinc-100">{t("handover.details_title")}</h3>
      <Field label={t("handover.km_label") + " *"}>
        <input
          type="number" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)}
          data-testid="fahrer-handover-km"
          className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-lg text-base font-mono"
        />
      </Field>
      <Field label={t("handover.fuel_pct_label")}>
        <input
          type="number" inputMode="numeric" min="0" max="100" value={fuel} onChange={(e) => setFuel(e.target.value)}
          data-testid="fahrer-handover-fuel"
          className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-lg text-base font-mono"
        />
      </Field>
      <Field label={t("handover.damages_label")}>
        <textarea
          rows={2} value={damages} onChange={(e) => setDamages(e.target.value)}
          data-testid="fahrer-handover-damages"
          placeholder={t("handover.damages_placeholder")}
          className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-lg text-sm"
        />
      </Field>
      <Field label={t("handover.notes_label")}>
        <textarea
          rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          data-testid="fahrer-handover-notes"
          className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-lg text-sm"
        />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SignatureStep({ value, onChange, t }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  function getPoint(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const touch = e.touches?.[0];
    return {
      x: ((touch?.clientX ?? e.clientX) - rect.left) * (c.width / rect.width),
      y: ((touch?.clientY ?? e.clientY) - rect.top) * (c.height / rect.height),
    };
  }
  function start(e) {
    e.preventDefault();
    isDrawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = getPoint(e);
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  }
  function move(e) {
    if (!isDrawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPoint(e);
    ctx.lineTo(p.x, p.y); ctx.stroke();
  }
  function end() {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  }
  function clear() {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange(null);
  }

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#fff"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round";
  }, []);

  return (
    <div className="space-y-3" data-testid="fahrer-signature-step">
      <div>
        <h3 className="text-base font-semibold text-zinc-100">{t("handover.signature_title")}</h3>
        <p className="text-xs text-zinc-500 mt-0.5">{t("handover.signature_hint")}</p>
      </div>
      <div className="bg-zinc-900 border-2 border-dashed border-zinc-700 rounded-lg p-2">
        <canvas
          ref={canvasRef}
          width={760}
          height={280}
          data-testid="fahrer-signature-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="w-full h-44 bg-zinc-950 rounded touch-none"
        />
      </div>
      <button
        onClick={clear}
        data-testid="fahrer-signature-clear"
        className="text-xs text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1"
      >
        <Eraser className="w-3 h-3" /> {t("handover.signature_clear")}
      </button>
    </div>
  );
}

// ─── 3. Active handover summary + return flow ─────────────────
function ActiveHandoverSummary({ handover, onReload }) {
  const { t } = useTranslation();
  const [returning, setReturning] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  if (returning) return <ReturnFlow handover={handover} onDone={onReload} onCancel={() => setReturning(false)} />;

  async function downloadPdf() {
    setPdfBusy(true);
    try {
      const res = await fahrerApi.get(`/fahrer/handover/${handover.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Uebergabe_${handover.vehicle_plate || handover.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally { setPdfBusy(false); }
  }

  const since = fmtDateTime(handover.finalized_at || handover.created_at);
  return (
    <div className="space-y-4" data-testid="fahrer-active-handover-summary">
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
        <div className="flex items-center gap-2 text-emerald-300 mb-2">
          <Check className="w-5 h-5" />
          <span className="text-sm font-bold">{t("handover.active_uebernahme")}</span>
        </div>
        <div className="text-base font-mono font-bold text-zinc-50">{handover.vehicle_label}</div>
        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
          <div>
            <div className="text-zinc-500">KM</div>
            <div className="font-mono text-zinc-200">{(handover.km || 0).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-zinc-500">{t("handover.since")}</div>
            <div className="font-mono text-zinc-200">{since}</div>
          </div>
        </div>
      </div>
      <button
        onClick={downloadPdf}
        disabled={pdfBusy}
        data-testid="fahrer-handover-download-pdf"
        className="w-full py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-medium rounded-lg text-sm inline-flex items-center justify-center gap-2"
      >
        <FileDown className="w-4 h-4" /> {pdfBusy ? "…" : t("handover.download_protocol")}
      </button>
      <button
        onClick={() => setReturning(true)}
        data-testid="fahrer-handover-start-return"
        className="w-full py-3.5 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg"
      >
        {t("handover.return_vehicle")}
      </button>
    </div>
  );
}

function ReturnFlow({ handover, onDone, onCancel }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);  // 1 = photos, 2 = details, 3 = signature
  const [photos, setPhotos] = useState(handover.dvic_return_photos || []);
  const [km, setKm] = useState("");
  const [fuel, setFuel] = useState("");
  const [damages, setDamages] = useState("");
  const [notes, setNotes] = useState("");
  const [sig, setSig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const allPhotos = photos.length === DVIC_POSITIONS.length;

  function refreshPhotos() {
    fahrerApi.get(`/fahrer/handover/${handover.id}`).then(({ data }) =>
      setPhotos(data.dvic_return_photos || [])
    );
  }
  async function uploadPhoto(position, file) {
    // Phase 5Z.42 — compress before upload (return phase)
    const compressed = await compressImage(file, { maxDimension: 1920, jpegQuality: 0.82 });
    const fd = new FormData();
    fd.append("position", position);
    fd.append("file", compressed);
    fd.append("phase", "return");
    try {
      await fahrerApi.post(`/fahrer/handover/${handover.id}/dvic-photo`, fd);
      refreshPhotos();
      toast.success("OK");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    }
  }
  async function deletePhoto(position) {
    try {
      await fahrerApi.delete(`/fahrer/handover/${handover.id}/dvic-photo/${position}?phase=return`);
      refreshPhotos();
    } catch { /* ignore */ }
  }

  async function submit() {
    if (!allPhotos) { toast.error(t("handover.missing_photos", "Alle 8 Fotos erforderlich")); setStep(1); return; }
    if (!km) { toast.error(t("handover.missing_km")); setStep(2); return; }
    if (Number(km) < (handover.km || 0)) { toast.error(t("handover.km_must_be_higher")); setStep(2); return; }
    if (!sig) { toast.error(t("handover.missing_signature")); setStep(3); return; }
    setSubmitting(true);
    try {
      await fahrerApi.post(`/fahrer/handover/${handover.id}/return`, {
        km: Number(km),
        fuel_pct: fuel === "" ? null : Number(fuel),
        notes,
        damages,
        signature_dataurl: sig,
      });
      toast.success("OK");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally { setSubmitting(false); }
  }

  return (
    <div className="space-y-4" data-testid="fahrer-return-flow">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3.5">
        <div className="flex items-center gap-3">
          <Truck className="w-5 h-5 text-amber-400" />
          <div className="flex-1">
            <div className="text-xs text-zinc-500">{t("handover.return_title")}</div>
            <div className="text-sm font-mono font-bold text-zinc-100">{handover.vehicle_label}</div>
          </div>
          <button onClick={onCancel} className="text-zinc-400 text-xs px-2 py-1" data-testid="fahrer-return-cancel">
            {t("common.cancel")}
          </button>
        </div>
        <div className="flex gap-1 mt-3">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`flex-1 h-1 rounded ${step >= s ? "bg-amber-500" : "bg-zinc-800"}`} />
          ))}
        </div>
        <div className="text-[10px] text-zinc-500 mt-1.5 flex justify-between">
          <span className={step === 1 ? "text-amber-400 font-medium" : ""}>1. Fotos</span>
          <span className={step === 2 ? "text-amber-400 font-medium" : ""}>2. Details</span>
          <span className={step === 3 ? "text-amber-400 font-medium" : ""}>3. Unterschrift</span>
        </div>
      </div>

      {step === 1 && (
        <DvicStep photos={photos} onUpload={uploadPhoto} onDelete={deletePhoto} t={t} handoverId={handover.id} phase="return" />
      )}
      {step === 2 && (
        <DetailsStep km={km} setKm={setKm} fuel={fuel} setFuel={setFuel} damages={damages} setDamages={setDamages} notes={notes} setNotes={setNotes} t={t} />
      )}
      {step === 3 && (
        <SignatureStep value={sig} onChange={setSig} t={t} />
      )}

      <div className="flex gap-2">
        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            data-testid="fahrer-return-prev"
            className="flex-1 py-3 bg-zinc-800 text-zinc-100 rounded-lg text-sm"
          >
            ← Zurück
          </button>
        )}
        {step < 3 ? (
          <button
            onClick={() => {
              if (step === 1 && !allPhotos) { toast.error(t("handover.missing_photos", "Alle 8 Fotos erforderlich")); return; }
              if (step === 2 && (!km || Number(km) < 0)) { toast.error(t("handover.missing_km")); return; }
              setStep(step + 1);
            }}
            data-testid="fahrer-return-next"
            className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold"
          >
            Weiter →
          </button>
        ) : (
          <button
            onClick={submit} disabled={submitting}
            data-testid="fahrer-return-finalize"
            className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-800 text-white rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            {submitting ? "…" : t("handover.return_finalize")}
          </button>
        )}
      </div>
    </div>
  );
}
